import {
    Link,
    Table,
    TableBody,
    TableCell,
    TableCellSelectRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableHeaderSelectAll,
    TableRow,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import React, { Fragment } from 'react';
import type { IAdminListProcess } from '@common/interfaces';
import type { IAdminProcess, NewOrOldAdminProcess } from '@frontend/components/interfaces/adminProcessesList';
import { ManagerAuthorization } from '@common/enums';
import { FullName } from '@frontend/components/elements/common';
import { getSelectedLanguage } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import { NullableFormattedDatetime } from '../../../elements/NullableFormattedDatetime';
import { NullableStringBirthDate } from '../../../elements/NullableStringBirthDate';
import { ProcessAdminStatus } from '../../../elements/processStatus/ProcessAdminStatus';
import { StatusLabelManager } from '../../../elements/StatusLabelManager';
import { isAdminProcess } from '../adminProcessesHelperFunctions';

interface IAdminProcessesTableProps {
    processes: NewOrOldAdminProcess[];
    setProcesses: (processes: NewOrOldAdminProcess[]) => void;
}

export function AdminProcessesTable(props: IAdminProcessesTableProps) {
    const navigate = useNavigate();
    const language = getSelectedLanguage();
    const canEdit = hasOneAuthorization(ManagerAuthorization.adminProcessEdit);

    const processesWithCheckedProp = props.processes.filter((process) => isAdminProcess(process));

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeaderSelectAll
                            checked={
                                (countSelectedProcesses(props.processes) === processesWithCheckedProp.length ||
                                    processesWithCheckedProp.some(
                                        (process) => isAdminProcess(process) && process.checked
                                    )) &&
                                processesWithCheckedProp.length > 0
                            }
                            indeterminate={
                                !processesWithCheckedProp.every((process) => isAdminProcess(process) && process.checked)
                            }
                            onSelect={(event) => setAllCheckBoxes((event.target as HTMLInputElement).checked)}
                        />
                        <TableHeader>{language.service.service}</TableHeader>
                        <TableHeader>{language.common.pid}</TableHeader>
                        <TableHeader>{language.patient.patient}</TableHeader>
                        <TableHeader>{language.patient.birthDateShort}</TableHeader>
                        <TableHeader>{language.patient.careProvider}</TableHeader>
                        <TableHeader>{language.common.liesWith}</TableHeader>
                        <TableHeader>{language.common.created}</TableHeader>
                        <TableHeader>{language.common.status}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.processes.map((relatedProcess, i) => (
                        <TableRow
                            key={'relatedProcess-' + i}
                            onClick={() =>
                                navigate(`/processes/${relatedProcess.processId ? '' : 'new/'}${relatedProcess.id}`)
                            }
                        >
                            {relatedProcess.processId && canEdit ? (
                                <TableCellSelectRow
                                    isSelected={relatedProcess.checked}
                                    onClick={(e) => e.stopPropagation()}
                                    onSelect={async (e) =>
                                        setCheckBox((e.target as HTMLInputElement).checked, relatedProcess.id)
                                    }
                                />
                            ) : (
                                <TableCell />
                            )}
                            <TableCell>{relatedProcess.service.code}</TableCell>
                            <TableCell>
                                <Link
                                    to={`/processes/${relatedProcess.processId ? '' : 'new/'}${relatedProcess.id}`}
                                    variant="link"
                                >
                                    <strong>
                                        {relatedProcess.processId ||
                                            (relatedProcess as IAdminListProcess).processNumber}
                                    </strong>
                                </Link>
                            </TableCell>
                            <TableCell>
                                <FullName {...relatedProcess.patient} />
                            </TableCell>
                            <TableCell>
                                <NullableStringBirthDate value={relatedProcess.patient.birthDate} />
                            </TableCell>
                            <TableCell>
                                <FullName {...relatedProcess.patient.careProviderPractitioner} />
                            </TableCell>
                            <TableCell>
                                {relatedProcess.processId && <strong>{getName(relatedProcess)}</strong>}
                                {(relatedProcess as IAdminListProcess).processNumber && (
                                    <strong>{getTaskNames(relatedProcess as IAdminListProcess)}</strong>
                                )}
                            </TableCell>
                            <TableCell>
                                <NullableFormattedDatetime value={relatedProcess.created_at} />
                            </TableCell>
                            <TableCell>
                                {relatedProcess.processId && (
                                    <StatusLabelManager
                                        badge
                                        request={relatedProcess}
                                    />
                                )}
                                {(relatedProcess as IAdminListProcess).processNumber &&
                                    getTaskLabels(relatedProcess as IAdminListProcess)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    function getName(process: IAdminProcess) {
        if (process.processId) {
            if (process.recipientPractitioner) {
                return <FullName {...process.recipientPractitioner} />;
            } else if (process.recipientOrganization) {
                return process.recipientOrganization.name;
            }
        }

        return null;
    }

    function setAllCheckBoxes(value: boolean) {
        props.setProcesses(
            props.processes.map((process) => (process.processId ? { ...process, checked: value } : { ...process }))
        );
    }

    function setCheckBox(value: boolean, id: string) {
        const newProcesses = [...props.processes];
        const index = newProcesses.findIndex((process) => process.id === id);
        newProcesses[index] = { ...props.processes[index], checked: value } as IAdminProcess;
        props.setProcesses(newProcesses);
    }

    function countSelectedProcesses(adminProcesses: NewOrOldAdminProcess[]) {
        return adminProcesses.filter((process) => process.processId && process.checked).length;
    }

    function getTaskNames(process: IAdminListProcess) {
        return (
            <>
                {process.tasks.map((task, i) => (
                    <React.Fragment key={task.id}>
                        {task.performerPractitioner ? (
                            <FullName {...task.performerPractitioner} />
                        ) : (
                            task.performerOrganization?.name || '-'
                        )}
                        {i < process.tasks.length - 1 && <br />}
                    </React.Fragment>
                ))}
            </>
        );
    }

    function getTaskLabels(process: IAdminListProcess) {
        return process.tasks.length === 0 ? (
            <ProcessAdminStatus status={process.status} />
        ) : (
            process.tasks.map((task, i) => (
                <Fragment key={task.id}>
                    <ProcessAdminStatus
                        status={process.status}
                        taskStatus={task.status}
                        taskType={task.type}
                    />
                    {i < process.tasks.length - 1 && <br />}
                </Fragment>
            ))
        );
    }
}
